import { useState, useEffect, useRef } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { motion } from 'motion/react';
import { useSession } from '@/lib/auth-client';
import { RiDatabase2Line, RiAddLine, RiDeleteBin6Line, RiPlayLine, RiTableLine, RiFlowChart, RiGridLine, RiListCheck, RiEditLine, RiCheckLine, RiCloseLine } from '@remixicon/react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { SidebarProvider, SidebarInset, SidebarTrigger } from '@/components/ui/sidebar';
import { Separator } from '@/components/ui/separator';
import { AppSidebar } from '@/components/app-sidebar';
import { ModeToggle } from '@/components/mode-toggle';
import { DatabaseConnectionDialog } from '@/components/database-connection-dialog';
import { EmptyState } from '@/components/empty-state';
import { Badge } from '@/components/ui/badge';
import { api, Database as DatabaseType } from '@/lib/api';
import { toast } from 'sonner';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbPage,
} from '@/components/ui/breadcrumb';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

export function DatabasesPage() {
  const { data: session } = useSession();
  const navigate = useNavigate();
  const [databases, setDatabases] = useState<DatabaseType[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showConnectionDialog, setShowConnectionDialog] = useState(false);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; database?: DatabaseType }>({
    open: false,
  });
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');
  const editInputRef = useRef<HTMLInputElement>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  const loadDatabases = async () => {
    if (!session?.user?.id) return;

    try {
      setIsLoading(true);
      const result = await api.listDatabases(session.user.id);
      setDatabases(result.databases);
    } catch (error: any) {
      toast.error('Failed to load databases');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadDatabases();
  }, [session?.user?.id]);

  const handleDelete = async (database: DatabaseType) => {
    if (!session?.user?.id) return;

    setIsDeleting(true);
    try {
      await api.deleteDatabase(database.id, session.user.id);
      toast.success('Database connection deleted');
      loadDatabases();
      setDeleteDialog({ open: false });
    } catch (error: any) {
      toast.error('Failed to delete database connection');
    } finally {
      setIsDeleting(false);
    }
  };

  const startEditing = (db: DatabaseType) => {
    setEditingId(db.id);
    setEditingName(db.name);
    setTimeout(() => {
      editInputRef.current?.focus();
      editInputRef.current?.select();
    }, 0);
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditingName('');
  };

  const saveEdit = async (db: DatabaseType) => {
    if (!session?.user?.id || !editingName.trim()) {
      cancelEditing();
      return;
    }

    if (editingName.trim() === db.name) {
      cancelEditing();
      return;
    }

    setIsUpdating(true);
    try {
      await api.updateDatabaseName(db.id, editingName.trim(), session.user.id);
      toast.success('Database name updated');
      loadDatabases();
      cancelEditing();
    } catch (error: any) {
      toast.error('Failed to update database name');
    } finally {
      setIsUpdating(false);
    }
  };

  const getDatabaseIcon = (type: string) => {
    const iconClass = "h-8 w-8 object-contain";
    switch (type) {
      case 'postgres':
        return <img src="/databases/postgres.svg" alt="PostgreSQL" className={iconClass} />;
      case 'mysql':
        return <img src="/databases/mysql.svg" alt="MySQL" className={iconClass} />;
      case 'sqlite':
        return <img src="/databases/sqlite.svg" alt="SQLite" className={iconClass} />;
      default:
        return <RiDatabase2Line className="h-8 w-8 text-primary" />;
    }
  };

  const getDatabaseTypeColor = (type: string) => {
    switch (type) {
      case 'postgres':
        return 'bg-blue-50 text-blue-700 dark:bg-blue-950/50 dark:text-blue-300 border border-blue-200 dark:border-blue-900';
      case 'mysql':
        return 'bg-orange-50 text-orange-700 dark:bg-orange-950/50 dark:text-orange-300 border border-orange-200 dark:border-orange-900';
      case 'sqlite':
        return 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-900';
      default:
        return 'bg-muted text-muted-foreground border border-border';
    }
  };

  const getDatabaseTypeName = (type: string) => {
    switch (type) {
      case 'postgres':
        return 'PostgreSQL';
      case 'mysql':
        return 'MySQL';
      case 'sqlite':
        return 'SQLite';
      default:
        return type.toUpperCase();
    }
  };

  const getDatabaseConnectionString = (db: DatabaseType) => {
    if (db.type === 'sqlite') {
      return 'Local Database';
    }
    return `${db.host}:${db.port}`;
  };

  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset>
        <header className="sticky top-0 flex h-16 shrink-0 items-center gap-2 border-b bg-background/80 backdrop-blur-xl supports-[backdrop-filter]:bg-background/80 px-4 z-10 shadow-sm">
          <SidebarTrigger className="-ml-1" />
          <Separator orientation="vertical" className="mr-2 h-4" />
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbPage>Databases</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
          <div className="ml-auto flex items-center gap-2">
            {databases.length > 0 && (
              <div className="flex items-center gap-1 mr-2 p-1 rounded-lg bg-muted/50">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setViewMode('grid')}
                  className={viewMode === 'grid' ? 'bg-background shadow-sm' : 'hover:bg-background/50'}
                >
                  <RiGridLine className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setViewMode('list')}
                  className={viewMode === 'list' ? 'bg-background shadow-sm' : 'hover:bg-background/50'}
                >
                  <RiListCheck className="h-4 w-4" />
                </Button>
              </div>
            )}
            <Button onClick={() => setShowConnectionDialog(true)}>
              <RiAddLine className="h-4 w-4 mr-2" />
              Connect Database
            </Button>
            <ModeToggle />
          </div>
        </header>

        <main className="flex-1 p-4 md:p-6">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="space-y-6 max-w-7xl mx-auto"
          >
            {isLoading ? (
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {[1, 2, 3].map((i) => (
                  <Card key={i} className="animate-pulse">
                    <CardHeader>
                      <div className="h-8 w-8 bg-muted rounded mb-2" />
                      <div className="h-6 bg-muted rounded w-3/4" />
                      <div className="h-4 bg-muted rounded w-1/2 mt-2" />
                    </CardHeader>
                    <CardContent>
                      <div className="h-10 bg-muted rounded" />
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : databases.length === 0 ? (
              <Card>
                <CardContent className="py-8">
                  <EmptyState
                    title="No databases connected"
                    description="Get started by connecting your first database. Connect to PostgreSQL, MySQL, or SQLite databases."
                    action={{
                      label: 'Connect Database',
                      onClick: () => setShowConnectionDialog(true),
                      icon: <RiAddLine className="h-4 w-4" />,
                    }}
                    animationSize="md"
                  />
                </CardContent>
              </Card>
            ) : viewMode === 'grid' ? (
              <TooltipProvider>
                <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
                  {databases.map((db, index) => (
                    <motion.div
                      key={db.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                    >
                      <Card className="group hover:border-border/80 transition-all duration-200 overflow-hidden h-full">
                        <div className="p-6 space-y-4">
                          {/* Header */}
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex items-center gap-3 flex-1 min-w-0">
                              <div className="p-2.5 rounded-xl bg-gradient-to-br from-muted/50 to-muted/20 flex items-center justify-center">
                                {getDatabaseIcon(db.type)}
                              </div>
                              <div className="flex-1 min-w-0">
                                {editingId === db.id ? (
                                  <div className="flex items-center gap-1.5">
                                    <input
                                      ref={editInputRef}
                                      type="text"
                                      value={editingName}
                                      onChange={(e) => setEditingName(e.target.value)}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter' && !isUpdating) saveEdit(db);
                                        if (e.key === 'Escape' && !isUpdating) cancelEditing();
                                      }}
                                      disabled={isUpdating}
                                      className="flex-1 h-7 px-2 py-1 text-sm font-semibold rounded-md border-2 border-border bg-card focus:outline-none focus:border-primary disabled:opacity-50 disabled:cursor-not-allowed"
                                    />
                                    <button
                                      onClick={() => saveEdit(db)}
                                      disabled={isUpdating}
                                      className="p-1 rounded hover:bg-muted transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                      <RiCheckLine className="h-3.5 w-3.5 text-green-600" />
                                    </button>
                                    <button
                                      onClick={cancelEditing}
                                      disabled={isUpdating}
                                      className="p-1 rounded hover:bg-muted transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                      <RiCloseLine className="h-3.5 w-3.5 text-muted-foreground" />
                                    </button>
                                  </div>
                                ) : (
                                  <div className="flex items-center gap-2 group/name">
                                    <h3 className="font-semibold text-base truncate">{db.name}</h3>
                                    <button
                                      onClick={() => startEditing(db)}
                                      className="opacity-0 group-hover/name:opacity-100 p-1 rounded hover:bg-muted transition-all"
                                    >
                                      <RiEditLine className="h-3.5 w-3.5 text-muted-foreground" />
                                    </button>
                                  </div>
                                )}
                                <p className="text-xs text-muted-foreground mt-0.5">
                                  {getDatabaseConnectionString(db)}
                                </p>
                              </div>
                            </div>
                            <Badge className={`${getDatabaseTypeColor(db.type)} text-xs font-medium px-2.5 py-1 rounded-md`}>
                              {getDatabaseTypeName(db.type)}
                            </Badge>
                          </div>

                          {/* Database Info */}
                          <div className="pt-2 pb-3 border-t border-border/50">
                            <p className="text-xs text-muted-foreground truncate">
                              {db.type === 'sqlite' ? db.filePath : db.database}
                            </p>
                          </div>

                          {/* Actions */}
                          <div className="flex gap-2">
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  size="sm"
                                  className="flex-1"
                                  onClick={() => navigate({ to: `/query/${db.id}` })}
                                >
                                  <RiPlayLine className="h-3.5 w-3.5 mr-1.5" />
                                  Query
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Run SQL queries</TooltipContent>
                            </Tooltip>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => navigate({ to: `/visual-query-builder/${db.id}` })}
                                >
                                  <RiFlowChart className="h-3.5 w-3.5" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Visual query builder</TooltipContent>
                            </Tooltip>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => navigate({ to: `/data-browser/${db.id}` })}
                                >
                                  <RiTableLine className="h-3.5 w-3.5" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Browse tables</TooltipContent>
                            </Tooltip>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => setDeleteDialog({ open: true, database: db })}
                                  className="text-destructive hover:text-destructive hover:bg-destructive/10 focus-visible:ring-destructive/30"
                                >
                                  <RiDeleteBin6Line className="h-3.5 w-3.5" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Delete connection</TooltipContent>
                            </Tooltip>
                          </div>
                        </div>
                      </Card>
                    </motion.div>
                  ))}
                </div>
              </TooltipProvider>
            ) : (
              <TooltipProvider>
                <div className="space-y-3">
                  {databases.map((db, index) => (
                    <motion.div
                      key={db.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.05 }}
                    >
                      <Card className="group hover:border-border/80 transition-all duration-200">
                        <div className="p-5">
                          <div className="flex items-center gap-4">
                            {/* Icon & Info */}
                            <div className="p-2.5 rounded-xl bg-gradient-to-br from-muted/50 to-muted/20 flex items-center justify-center">
                              {getDatabaseIcon(db.type)}
                            </div>

                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                {editingId === db.id ? (
                                  <div className="flex items-center gap-1.5 flex-1">
                                    <input
                                      ref={editInputRef}
                                      type="text"
                                      value={editingName}
                                      onChange={(e) => setEditingName(e.target.value)}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter' && !isUpdating) saveEdit(db);
                                        if (e.key === 'Escape' && !isUpdating) cancelEditing();
                                      }}
                                      disabled={isUpdating}
                                      className="flex-1 h-7 px-2 py-1 text-sm font-semibold rounded-md border-2 border-border bg-card focus:outline-none focus:border-primary max-w-xs disabled:opacity-50 disabled:cursor-not-allowed"
                                    />
                                    <button
                                      onClick={() => saveEdit(db)}
                                      disabled={isUpdating}
                                      className="p-1 rounded hover:bg-muted transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                      <RiCheckLine className="h-3.5 w-3.5 text-green-600" />
                                    </button>
                                    <button
                                      onClick={cancelEditing}
                                      disabled={isUpdating}
                                      className="p-1 rounded hover:bg-muted transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                      <RiCloseLine className="h-3.5 w-3.5 text-muted-foreground" />
                                    </button>
                                  </div>
                                ) : (
                                  <div className="flex items-center gap-2 group/name">
                                    <h3 className="font-semibold text-base truncate">{db.name}</h3>
                                    <button
                                      onClick={() => startEditing(db)}
                                      className="opacity-0 group-hover/name:opacity-100 p-1 rounded hover:bg-muted transition-all"
                                    >
                                      <RiEditLine className="h-3.5 w-3.5 text-muted-foreground" />
                                    </button>
                                  </div>
                                )}
                                <Badge className={`${getDatabaseTypeColor(db.type)} text-xs font-medium px-2.5 py-1 rounded-md`}>
                                  {getDatabaseTypeName(db.type)}
                                </Badge>
                              </div>
                              <div className="flex items-center gap-3 text-xs text-muted-foreground">
                                <span>{getDatabaseConnectionString(db)}</span>
                                <span className="text-border">•</span>
                                <span className="truncate">{db.type === 'sqlite' ? db.filePath : db.database}</span>
                              </div>
                            </div>

                            {/* Actions */}
                            <div className="flex items-center gap-2">
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    size="sm"
                                    onClick={() => navigate({ to: `/query/${db.id}` })}
                                  >
                                    <RiPlayLine className="h-3.5 w-3.5 mr-1.5" />
                                    Query
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>Run SQL queries</TooltipContent>
                              </Tooltip>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => navigate({ to: `/visual-query-builder/${db.id}` })}
                                  >
                                    <RiFlowChart className="h-3.5 w-3.5 mr-1.5" />
                                    Visual
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>Visual query builder</TooltipContent>
                              </Tooltip>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => navigate({ to: `/data-browser/${db.id}` })}
                                  >
                                    <RiTableLine className="h-3.5 w-3.5 mr-1.5" />
                                    Browse
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>Browse tables</TooltipContent>
                              </Tooltip>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => setDeleteDialog({ open: true, database: db })}
                                    className="text-destructive hover:text-destructive hover:bg-destructive/10 focus-visible:ring-destructive/30"
                                  >
                                    <RiDeleteBin6Line className="h-3.5 w-3.5" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>Delete connection</TooltipContent>
                              </Tooltip>
                            </div>
                          </div>
                        </div>
                      </Card>
                    </motion.div>
                  ))}
                </div>
              </TooltipProvider>
            )}
          </motion.div>
        </main>
      </SidebarInset>

      <DatabaseConnectionDialog
        open={showConnectionDialog}
        onOpenChange={setShowConnectionDialog}
        onSuccess={loadDatabases}
        userId={session?.user?.id || ''}
      />

      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => !isDeleting && setDeleteDialog({ open })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete database connection?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove the connection to "{deleteDialog.database?.name}". Your actual
              database will not be affected.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => deleteDialog.database && handleDelete(deleteDialog.database)}
              disabled={isDeleting}
            >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </SidebarProvider>
  );
}
